import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const TesterCtx = createContext(null);

const STORAGE_KEY = 'tester_modality_active';

/** Modalidades válidas del backend. Deben coincidir con VALID_TESTER_MODALITIES. */
export const TESTER_MODALITIES = [
    { id: 'parvularia',          label: 'Educación Parvularia',              color: 'pink' },
    { id: 'basica_generalista',  label: 'Ed. Básica · Generalista',          color: 'indigo' },
    { id: 'basica_asignaturas',  label: 'Ed. Básica · Asignaturas',          color: 'sky' },
    { id: 'media',               label: 'Educación Media',                   color: 'emerald' },
];

/**
 * Provee la "modalidad activa de prueba" para cuentas testers.
 * NO modifica el perfil real del usuario. Los borradores/progreso se aíslan
 * por (user_id, modalidad) en el backend via header X-Tester-Modality.
 */
export function TesterModalityProvider({ children }) {
    const { user, isAuthenticated } = useAuth();
    const [isTester, setIsTester] = useState(false);
    const [activeModality, setActiveModalityState] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
    });

    // Consulta al backend si el usuario es tester multi-modalidad
    useEffect(() => {
        let cancelled = false;
        if (!isAuthenticated) {
            setIsTester(false);
            return;
        }
        (async () => {
            try {
                const r = await api.get('/tester/multimodality');
                if (!cancelled) setIsTester(!!r.data?.is_tester);
            } catch (_) {
                if (!cancelled) setIsTester(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, user?.id]);

    // Interceptor request: si tester activo, inyecta header X-Tester-Modality
    useEffect(() => {
        const id = api.interceptors.request.use((config) => {
            if (isTester && activeModality) {
                config.headers = config.headers || {};
                config.headers['X-Tester-Modality'] = activeModality;
            } else if (config.headers && 'X-Tester-Modality' in config.headers) {
                delete config.headers['X-Tester-Modality'];
            }
            return config;
        });
        return () => api.interceptors.request.eject(id);
    }, [isTester, activeModality]);

    const setActiveModality = useCallback((val) => {
        setActiveModalityState(val);
        try {
            if (val) localStorage.setItem(STORAGE_KEY, val);
            else localStorage.removeItem(STORAGE_KEY);
        } catch (_) { /* ignore quota errors */ }
        // Reload para que dispatchers (Dashboard/Rúbricas/Progreso) reevalúen
        // qué vista renderizar según la modalidad activa.
        setTimeout(() => window.location.reload(), 100);
    }, []);

    const clearModality = useCallback(() => setActiveModality(null), [setActiveModality]);

    const value = { isTester, activeModality, setActiveModality, clearModality };
    return <TesterCtx.Provider value={value}>{children}</TesterCtx.Provider>;
}

export function useTesterModality() {
    const ctx = useContext(TesterCtx);
    if (!ctx) return { isTester: false, activeModality: null, setActiveModality: () => {}, clearModality: () => {} };
    return ctx;
}

/** Discriminador global "es Parvularia" que considera el override de tester. */
export function useIsParvularia(perfil) {
    const { isTester, activeModality } = useTesterModality();
    if (isTester && activeModality) return activeModality === 'parvularia';
    const nivel = (perfil?.nivel || '').trim().toLowerCase();
    return nivel === 'educación parvularia' || nivel === 'educacion parvularia' || nivel === 'parvularia';
}
